import { FormApi } from "final-form";
import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import i18next from "src/i18n/config";
import { PermissionService } from "src/services/permissionService/permissionService";
import UserSettingsService from "src/services/userSettingsService";
import { useDispatch } from "src/store";
import { setCurrentWorkspaceId } from "src/store/app";

import { PageContainer } from "@dashboard/shared/components/PageContainer";
import PageHeader from "@dashboard/shared/components/PageHeader";
import { Row, SectionContainer, SectionHeader } from "@dashboard/shared/styles";
import { TabContext, TabPanel } from "@mui/lab";
import { Box, CheckboxProps, Tab, useTheme } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { ExpiredWrapper } from "@shared/ExpiredWrapper";
import { CheckboxField } from "@shared/form/CheckboxField";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import { ControlledSelectValue } from "@shared/form/SelectField/SelectField.types";
import { TextField } from "@shared/form/TextField";
import { LoadingBox } from "@shared/Loading";
import { PageTabList } from "@shared/PageTabList/PageTabList";
import { showSuccessToast } from "@shared/toasts/Toasts";
import { Tooltip } from "@shared/Tooltip";

import { clearAllFilters } from "../devices/store";

import { useAllWorkspaces, useCurrentWorkspace, useCurrentWorkspaceFeatures, useWorkspaceMemberRole } from "./api/helpers";
import { MembersTable } from "./components/MemberTable";
import { SubscriptionSection } from "./components/SubscriptionSection/SubscriptionSection";
import { WorkspaceConfigurationPanel } from "./components/WorkspaceConfigurationPanel/WorkspaceConfigurationPanel";
import { useUpdateWorkspaceMutation } from "./api";
import { useStyles } from "./WorkspacesPage.style";

const maxCharactersLength = 64;

type FormValues = {
    id: number;
    name: string;
    guid: string;
    organizationName: string | null;
    organizationVatId: string | null;
};

type WorkspacePageTab = "overview" | "members" | "subscription" | "configuration";

function resolveTabFromUrl(
    tabFromUrl: string | null,
    canViewSubscriptionTab: boolean,
    canAccessConfigurationTab: boolean,
): WorkspacePageTab {
    if (!tabFromUrl) {
        return "overview";
    }
    if (tabFromUrl === "overview" || tabFromUrl === "members") {
        return tabFromUrl;
    }
    if (tabFromUrl === "subscription") {
        return canViewSubscriptionTab ? "subscription" : "overview";
    }
    if (tabFromUrl === "configuration") {
        return canAccessConfigurationTab ? "configuration" : "overview";
    }
    return "overview";
}

function tryResolveTabFromUserAction(
    value: string,
    canViewSubscriptionTab: boolean,
    canAccessConfigurationTab: boolean,
): WorkspacePageTab | null {
    if (value === "overview" || value === "members") {
        return value;
    }
    if (value === "subscription") {
        return canViewSubscriptionTab ? "subscription" : null;
    }
    if (value === "configuration") {
        return canAccessConfigurationTab ? "configuration" : null;
    }
    return null;
}

const WorkspacesPage = () => {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation(["common", "pages"]);
    const theme = useTheme();

    const { data: workspace, isLoading: isCurrentWorkspaceLoading } = useCurrentWorkspace();
    const { data: workspaces = [], refetch, isLoading: isWorkspacesListLoading } = useAllWorkspaces();
    const { data: workspaceFeatures = [], isLoading: isWorkspaceFeaturesLoading } = useCurrentWorkspaceFeatures();
    const [updateWorkspace] = useUpdateWorkspaceMutation();

    const subscription = workspace?.subscription;
    const initialValues = _.omit(workspace, ["members", "subscription"]);

    const { workspaceAbility } = PermissionService();
    const allowedToEditWorkspace = workspaceAbility(workspace).can("update", "Workspace");

    const role = useWorkspaceMemberRole();
    const canAccessSubscriptionTab = role === "Owner" || role === "Manager";
    const canAccessConfigurationTab = role === "Owner";
    const hasSubscription = Boolean(subscription);
    const canViewSubscriptionTab = hasSubscription && canAccessSubscriptionTab;

    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get("tab");
    const [activeTab, setActiveTab] = useState(() =>
        resolveTabFromUrl(tabParam, canViewSubscriptionTab, canAccessConfigurationTab),
    );

    const handleTabChange = useCallback(
        (_event: React.SyntheticEvent, value: string) => {
            const next = tryResolveTabFromUserAction(value, canViewSubscriptionTab, canAccessConfigurationTab);
            if (next === null) {
                return;
            }
            setActiveTab(next);
            setSearchParams({ tab: next }, { replace: true });
        },
        [canViewSubscriptionTab, canAccessConfigurationTab, setSearchParams],
    );

    const handleWorkspaceChange = (value: ControlledSelectValue<false> | undefined) => {
        dispatch(setCurrentWorkspaceId(value));
        dispatch(clearAllFilters());
    };
    const validateWorkspaceDetails = (values: FormValues) => {
        const errors: Partial<FormValues> = {};

        if (!values.name) {
            errors.name = t("pages:workspacePage.input.name.validation.empty");
        } else if (values.name.length > maxCharactersLength) {
            errors.name = t("pages:workspacePage.input.name.validation.maxLength", { maxCharactersLength });
        }

        return errors;
    };
    const validateOrganizationalDetails = (values: FormValues) => {
        const errors: Partial<FormValues> = {};
        const organizationName = values.organizationName || "";
        const organizationVatId = values.organizationVatId || "";

        if (organizationName.length > maxCharactersLength) {
            errors.organizationName = t("pages:workspacePage.input.organizationName.validation.maxLength", {
                maxCharactersLength,
            });
        }

        if (organizationVatId.length > maxCharactersLength) {
            errors.organizationVatId = t("pages:workspacePage.input.organizationVatId.validation.maxLength", {
                maxCharactersLength,
            });
        }

        return errors;
    };
    const onSubmitWorkspaceDetails = async (values: FormValues) => {
        if (workspace?.id) {
            await updateWorkspace({
                ...workspace,
                organizationVatId: initialValues.organizationVatId,
                organizationName: initialValues.organizationName,
                name: values.name,
                guid: initialValues.guid,
            })
                .unwrap()
                .then(() => showSuccessToast(t("pages:workspacePage.toast.workspaceUpdateSuccess")));
        }
    };
    const onSubmitOrganizationalDetails = async (values: FormValues) => {
        if (workspace?.id) {
            await updateWorkspace({
                ...workspace,
                organizationVatId: values.organizationVatId,
                organizationName: values.organizationName,
                name: initialValues.name,
                guid: initialValues.guid,
            })
                .unwrap()
                .then(() => showSuccessToast(t("pages:workspacePage.toast.workspaceUpdateSuccess")));
        }
    };
    const onTerminatedChange: CheckboxProps["onChange"] = async (_e, checked) => {
        UserSettingsService.setCommonValue("includeTerminatedWorkspaces", checked);
        await refetch();
    };

    const submitOnBlur = (form: FormApi<FormValues>) => () => {
        const state = form.getState();
        if (state.dirty && !state.invalid && allowedToEditWorkspace) {
            form.submit();
        }
    };

    useEffect(() => {
        const resolved = resolveTabFromUrl(tabParam, canViewSubscriptionTab, canAccessConfigurationTab);
        setActiveTab(resolved);
        const effectiveUrlTab = tabParam && tabParam.length > 0 ? tabParam : null;
        const urlMatches = resolved === effectiveUrlTab || (effectiveUrlTab === null && resolved === "overview");
        if (!urlMatches) {
            setSearchParams({ tab: resolved }, { replace: true });
        }
    }, [workspace?.id, tabParam, canViewSubscriptionTab, canAccessConfigurationTab, setSearchParams]);

    const includeTerminatedWorkspaces = UserSettingsService.getIncludeTerminatedWorkspaces();

    const isWorkspacePageLoading = isWorkspacesListLoading || isCurrentWorkspaceLoading;

    if (isWorkspacePageLoading) {
        return (
            <PageContainer>
                <Box className={classes.loadingContainer}>
                    <LoadingBox sx={{ marginTop: 0 }} />
                </Box>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <PageHeader title={t("pages:workspacePage.title")} description={t("pages:workspacePage.description")} />
            <Form<FormValues>
                initialValues={initialValues}
                onSubmit={() => undefined}
                render={() => (
                    <Box className={`${classes.sectionContent} ${classes.workspaceSelectorAboveOverlay}`}>
                        <ControlledSelectField
                            name="id"
                            label={t("pages:workspacePage.input.currentWorkspace.caption")}
                            data-testid="select-field"
                            onChange={handleWorkspaceChange}
                            options={workspaces.map((item) => ({
                                label: item.name,
                                value: item.id,
                            }))}
                            fullWidth
                            searchable
                        />
                        <CheckboxField
                            key={String(includeTerminatedWorkspaces)}
                            defaultChecked={includeTerminatedWorkspaces}
                            label={t("pages:workspacePage.input.showTerminatedWorkspaces.caption")}
                            controlProps={{ style: { marginTop: theme.spacing(2) } }}
                            onChange={onTerminatedChange}
                        />
                    </Box>
                )}
            />
            <ExpiredWrapper>
                <TabContext value={activeTab}>
                    <PageTabList onChange={handleTabChange}>
                        <Tab
                            value="overview"
                            label={t("pages:workspacePage.tabs.overview")}
                            icon={<i className="fa-solid fa-briefcase" />}
                            iconPosition="start"
                        />
                        <Tab
                            value="members"
                            label={t("pages:workspacePage.tabs.members")}
                            icon={<i className="fa-solid fa-users" />}
                            iconPosition="start"
                        />
                        {hasSubscription ? (
                            canAccessSubscriptionTab ? (
                                <Tab
                                    value="subscription"
                                    label={t("pages:workspacePage.tabs.subscription")}
                                    icon={<i className="fa-solid fa-credit-card" />}
                                    iconPosition="start"
                                />
                            ) : (
                                <Tooltip title={t("pages:workspacePage.tooltip.subscriptionTabRestricted")}>
                                    <span style={{ display: "inline-flex" }}>
                                        <Tab
                                            value="subscription"
                                            label={t("pages:workspacePage.tabs.subscription")}
                                            icon={<i className="fa-solid fa-credit-card" />}
                                            iconPosition="start"
                                            disabled
                                        />
                                    </span>
                                </Tooltip>
                            )
                        ) : null}
                        {canAccessConfigurationTab ? (
                            <Tab
                                value="configuration"
                                label={t("pages:workspacePage.tabs.configuration")}
                                icon={<i className="fa-solid fa-gear" />}
                                iconPosition="start"
                            />
                        ) : (
                            <Tooltip title={t("pages:workspacePage.tooltip.configurationTabRestricted")}>
                                <span style={{ display: "inline-flex" }}>
                                    <Tab
                                        value="configuration"
                                        label={t("pages:workspacePage.tabs.configuration")}
                                        icon={<i className="fa-solid fa-gear" />}
                                        iconPosition="start"
                                        disabled
                                    />
                                </span>
                            </Tooltip>
                        )}
                    </PageTabList>

                    <TabPanel value="overview" className={classes.tabPanel}>
                        <Form<FormValues>
                            initialValues={initialValues}
                            onSubmit={onSubmitWorkspaceDetails}
                            validate={validateWorkspaceDetails}
                            render={({ form }) => (
                                <>
                                    <SectionHeader>{t("pages:workspacePage.section.title.workspaceDetails")}</SectionHeader>
                                    <Row className={classes.workspaceNameBox}>
                                        <Tooltip
                                            title={t("pages:workspacePage.tooltip.workspaceRoleChangeNotAllowed")}
                                            hide={allowedToEditWorkspace}
                                            childrenBoxStyles={{ display: "block", width: "100%" }}
                                        >
                                            <TextField
                                                name="name"
                                                label={t("pages:workspacePage.input.name.caption")}
                                                fullWidth
                                                disabled={!allowedToEditWorkspace}
                                                onBlur={submitOnBlur(form)}
                                            />
                                        </Tooltip>
                                    </Row>
                                    <Row className={classes.guidBox}>
                                        <TextField
                                            fullWidth
                                            name="guid"
                                            label={t("pages:workspacePage.input.guid.caption")}
                                            disabled
                                        />
                                    </Row>
                                    <Row>
                                        <CustomAlert severity="info">{t("pages:workspacePage.alert.workspaceGuid")}</CustomAlert>
                                    </Row>
                                </>
                            )}
                        />

                        <Form<FormValues>
                            initialValues={initialValues}
                            validate={validateOrganizationalDetails}
                            onSubmit={onSubmitOrganizationalDetails}
                            render={({ form }) => (
                                <SectionContainer>
                                    <SectionHeader>{t("pages:workspacePage.section.title.organizationDetails")}</SectionHeader>
                                    <Row className={classes.organizationNameBox}>
                                        <Tooltip
                                            title={t("pages:workspacePage.tooltip.workspaceRoleChangeNotAllowed")}
                                            hide={allowedToEditWorkspace}
                                            childrenBoxStyles={{ display: "block", width: "100%" }}
                                        >
                                            <TextField
                                                name="organizationName"
                                                disabled={!allowedToEditWorkspace}
                                                label={t("common:label.organizationName")}
                                                fullWidth
                                                onBlur={submitOnBlur(form)}
                                            />
                                        </Tooltip>
                                    </Row>

                                    <Row className="mt-2">
                                        <Tooltip
                                            title={t("pages:workspacePage.tooltip.workspaceRoleChangeNotAllowed")}
                                            hide={allowedToEditWorkspace}
                                            childrenBoxStyles={{ display: "block", width: "100%" }}
                                        >
                                            <TextField
                                                name="organizationVatId"
                                                className={classes.organizationVatId}
                                                disabled={!allowedToEditWorkspace}
                                                label={t("pages:workspacePage.input.organizationVatId.caption")}
                                                fullWidth
                                                onBlur={submitOnBlur(form)}
                                            />
                                        </Tooltip>
                                    </Row>
                                </SectionContainer>
                            )}
                        />
                    </TabPanel>

                    <TabPanel value="members" className={classes.tabPanel}>
                        {workspace && <MembersTable workspace={workspace} />}
                    </TabPanel>

                    {canViewSubscriptionTab && subscription ? (
                        <TabPanel value="subscription" className={classes.tabPanel}>
                            <SubscriptionSection
                                subscription={subscription}
                                features={workspaceFeatures}
                                isLoading={isWorkspaceFeaturesLoading}
                            />
                        </TabPanel>
                    ) : null}

                    {canAccessConfigurationTab && workspace ? (
                        <TabPanel value="configuration" className={classes.tabPanel}>
                            <WorkspaceConfigurationPanel />
                        </TabPanel>
                    ) : null}
                </TabContext>
            </ExpiredWrapper>
        </PageContainer>
    );
};

export default withMetaDescription(WorkspacesPage, {
    title: i18next.t("pages:workspacePage.metaTitle"),
    description: i18next.t("pages:workspacePage.metaDescription"),
});
